import { Form, useFetcher, useLoaderData } from '@remix-run/react'
import React, { forwardRef, useEffect, useState } from 'react'

import {
  Button,
  Card,
  ContentPicpay,
  ContentPix,
  Label,
  LoadingDots,
  OptionPayment,
  Radio,
  RadioGroup,
  TextField,
} from '../primitives'
import PaymentCreditCart from './CreditCart'
import { useGlobalToast } from '~/hooks/useGlobalToast'
import { Add, CreditCard, Picpay, Pix } from '~/icons'
import SvgAdd from '~/icons/Add'
import SvgCash from '~/icons/Cash'
import SvgRemove from '~/icons/Remove'
import { cn } from '~/lib/cn'
import type { loader } from '~/routes/checkout.payment'
import type { InstallmentOption, PaymentSystem } from '~/type/Cart'

interface PaymentOptionProps extends Pick<PaymentSystem, 'name'>, Pick<InstallmentOption, 'paymentSystem'> {
  icon?: React.ReactNode
}

interface SelectPaymentProps {
  onChangePayment: (selectedPayment: string) => void
}

const CREDIT_CARD_SYSTEM_NAME = 'credit-card'

const SelectPayment = forwardRef(({ onChangePayment }: SelectPaymentProps, ref) => {
  const {
    paymentData: { paymentSystems },
  } = useLoaderData<typeof loader>()
  const [selectedPayment, setSelectedPayment] = useState('')
  const [responseVoucher, setResponseVoucher] = useState<string | undefined>(undefined)
  const [voucher, setVoucher] = useState('')
  const [selectedOption, setSelectedOption] = useState<string | null>(null)
  const fetcher = useFetcher()
  const activePaymentOptions = paymentSystems.map((payment: PaymentSystem) => payment.id)
  const { show } = useGlobalToast()
  const creditCardPaymentIds = ['1', '2', '4', '9']
  const creditCardPaymentGroup = creditCardPaymentIds.filter((payment) =>
    activePaymentOptions.includes(payment),
  )
  const paymentOptions: PaymentOptionProps[] = [
    {
      paymentSystem: CREDIT_CARD_SYSTEM_NAME,
      name: 'Cartão de crédito',
      icon: <CreditCard className="fill-gray-100 text-xl" />,
    },
    { paymentSystem: '125', name: 'Pix', icon: <Pix className="fill-gray-100 text-xl" /> },
    { paymentSystem: '72', name: 'Pic Pay', icon: <Picpay className="fill-gray-100 text-xl" /> },
    { paymentSystem: '201', name: 'Pagar na entrega', icon: <SvgCash className="fill-gray-100 text-xl" /> },
    { paymentSystem: '47', name: 'Boleto', icon: <SvgCash className="fill-gray-100 text-xl" /> },
  ].filter((payment) => {
    return activePaymentOptions.includes(payment.paymentSystem)
  })

  const isCreditCardActive = activePaymentOptions.find((option) => creditCardPaymentIds.includes(option))

  const isVoucherActive = activePaymentOptions.includes('16')

  const handlePaymentChange = (value: string) => {
    setSelectedPayment(value)
    setSelectedOption('')

    onChangePayment(value)

    fetcher.submit(
      { paymentSystem: value, actionType: 'selectPayment' },

      { method: 'post', action: '/checkout/payment' },
    )
  }

  const handleOptionPaymentClick = (option: string) => {
    setSelectedPayment('')
    setVoucher('')
    setSelectedOption(option === selectedOption ? null : option)
  }

  const isCreditCardSelected = creditCardPaymentGroup.includes(selectedPayment)

  const handleChangeVoucher = (value: string) => {
    setVoucher(value)
  }

  const addVoucher = (e: React.FormEvent) => {
    e.preventDefault()
    debugger
    const formData = new FormData(e.target as HTMLFormElement)
    const voucher = formData.get('voucher') as string
    fetcher.submit(
      { actionType: 'addVoucher', voucher: voucher },
      { method: 'post', action: '/checkout/payment' },
    )
  }

  // TODO implementar após finalizar o TDI-1913
  // const removeVoucher = (e: React.FormEvent) => {
  //   e.preventDefault()
  //   setVoucher('')
  // }

  useEffect(() => {
    if (fetcher.data && fetcher.state.includes('idle')) {
      setResponseVoucher(fetcher.data.toString()) // TODO implementar, pegando o retorno, após o TDI-1913

      // TODO implementar corretamente o Toast após TDI-1913
      show({ type: 'success', message: 'Vale compras adicionado com sucesso' })
    }
  }, [fetcher.data])

  return (
    <>
      <h2 className="font-medium text-sm">Selecione o método de Pagamento:</h2>
      <RadioGroup value={selectedPayment} onChange={handlePaymentChange}>
        {isCreditCardActive ? (
          <>
            <Card
              as="label"
              className={cn(
                selectedPayment === CREDIT_CARD_SYSTEM_NAME || isCreditCardSelected
                  ? 'ring-primary-orange ring-1'
                  : '',
              )}
              color="secondary"
            >
              <Radio value={creditCardPaymentGroup[0]}>
                Cartão de crédito
                <CreditCard className="fill-gray-100 text-xl" />
              </Radio>
            </Card>
            {selectedPayment === creditCardPaymentGroup[0] && <PaymentCreditCart />}
          </>
        ) : null}

        <div className="flex flex-col lg:flex-row gap-4">
          {paymentOptions
            .filter((option) => option.paymentSystem !== CREDIT_CARD_SYSTEM_NAME)
            .map((payment) => (
              <div className="w-full lg:w-1/2 flex flex-col gap-4" key={payment.paymentSystem}>
                <Card
                  as="label"
                  className={cn(
                    'w-full',
                    selectedPayment === payment.paymentSystem ? 'ring-primary-orange ring-1' : '',
                  )}
                  color="secondary"
                >
                  <Radio value={payment.paymentSystem}>
                    {payment.name}
                    {payment.icon}
                  </Radio>
                </Card>
                {selectedPayment === payment.paymentSystem && payment.name === 'Pix' && <ContentPix />}
                {selectedPayment === payment.paymentSystem && payment.name === 'Pic Pay' && <ContentPicpay />}
              </div>
            ))}
        </div>
      </RadioGroup>
      {/* {isVoucherActive ? (
        <>
          <Label className="text-sm font-medium pt-2">Possui vale compras ou cartão Presente?</Label>
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="w-full lg:w-1/2 flex flex-col gap-4">
              <OptionPayment
                icon={<Add className="fill-gray-100 text-xl" />}
                isSelected={selectedOption === 'voucher'}
                text="Adicionar vale compras?"
                onClick={() => handleOptionPaymentClick('voucher')}
              />
              {selectedOption === 'voucher' && (
                // TODO implementar removeVoucher após TDI-1913
                <Form action="/checkout/cart" method="delete" onSubmit={addVoucher}>
                  <Card color="secondary">
                    <TextField
                      defaultValue={voucher}
                      description="Digite o número do seu vale compras"
                      label="Vale compras *"
                      name="voucher"
                      type="text"
                      value={voucher}
                      isRequired
                      onChange={handleChangeVoucher}
                    />

                    {responseVoucher && (
                      <Button
                        className="gap-2 flex border-2 border-gray-20 rounded disabled:bg-gray-20 text-gray-100 bg-transparent
                    ml-auto mt-1 items-center justify-center text-right md:w-[140px] w-full h-[38px]
                    text-base"
                        type="submit"
                      >
                        <>
                          <SvgRemove />
                          <p>Remover</p>
                        </>
                      </Button>
                    )}

                    {!responseVoucher && (
                      <Button
                        className="gap-2 flex border-2 border-gray-20 rounded disabled:bg-gray-20 text-gray-100 bg-transparent
                    ml-auto mt-1 items-center justify-center text-right md:w-[140px] w-full h-[38px]
                    text-base"
                        type="submit"
                      >
                        {fetcher.state === 'submitting' ? (
                          <LoadingDots color="black" />
                        ) : (
                          <>
                            <SvgAdd />
                            <p>Adicionar</p>
                          </>
                        )}
                      </Button>
                    )}
                  </Card>
                </Form>
              )}
            </div>
            <div className="w-full lg:w-1/2 flex flex-col gap-4">
              <OptionPayment
                icon={<Add className="fill-gray-100 text-xl" />}
                isSelected={selectedOption === 'giftCard'}
                text="Adicionar cartão presente"
                onClick={() => handleOptionPaymentClick('giftCard')}
              />
              {selectedOption === 'giftCard' && <p>cartão presente</p>}
            </div>
          </div>
        </>
      ) : null} */}
    </>
  )
})

SelectPayment.displayName = 'SelectPayment'
export default SelectPayment
